import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setHeaderTabs, setHeaderTitle } from '../../../redux/App/App.slice';

import {
  NOT_ASSIGN_LABEL,
  chartVarieties,
  reportHeader,
} from '../../../constants/report.constant';
import CardComponent from '../../../components/Card.component';
import ReportParamsForm from '../../forms/Report/Params.form';
import {
  addToQueryString,
  getFilters,
  getQueryStringFrom,
  parseSearchParamsRouter,
} from '../../../services/router.service';

import { getReportThunk } from '../../../redux/Report/Report.thunk';

import { getReport, getReportParams } from '../../../redux/Report/Report.slice';

import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';

import { getDateTime } from '../../../services/date.service';
import { mapReportParamsToCandidates } from '../../../services/mappings.service';
import { saveAs } from '../../../utils/file';

import { Report } from '../../../redux/Report/Report.type';

import ChartComponent, {
  ChartDataItem,
  MultipleChartDataItem,
} from '../../../components/Chart/Chart.component';
import NoResultComponent from '../../../components/NoResult.component';
import FloatButtonComponent from '../../../components/FloatButton.component';
import PictureCapturingComponent from '../../../components/PictureCapturing.component';

import { RoutesConstant } from '../../../constants/routes.constant';
import { DATE_YEAR_MONTH_DAY_FORMAT } from '../../../constants/date.contant';

import DownloadIcon from '../../../components/Icons/Download.icon';
import {
  StyledCommonCountWrapper,
  StyledPictureLegendContainer,
  StyledReportPage,
  StyledRightPanelTitle,
} from './Report.styles';
import { MemoizedChart } from './components/Chart/Chart.component';
import {
  DEFAULT_CHART_TYPE,
  DEFAULT_GROUP,
  DOWNLOAD_ICON_SIZE,
  READABLE_ROWS_COUNT,
} from './constants/constants';
import { getLocalStorage } from '../../../utils/storage';
import { getUserRoles } from '../../../redux/User/UserSlice';
import { getRolesByPermissionToFullReportView, UserRole } from '../../../types/userRole.types';
import { LocationState } from '../../../types/locationState.interface';

const ReportPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { group } = useParams();
  const [, setSearchParams] = useSearchParams();
  const report = useAppSelector(getReport);
  const reportParams = useAppSelector(getReportParams);
  const [chartType, setChartType] = useState(DEFAULT_CHART_TYPE);
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useAppSelector(getUserRoles);

  const captureRef = useRef({ capture: () => Promise.resolve(new Blob()) });
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartLegendRef = useRef<HTMLDivElement | null>(null);
  const pictureLegendRef = useRef<HTMLDivElement | null>(null);

  const updateChartType = (newType: string) => {
    const search = addToQueryString([{
      name: 'chartType',
      value: newType === DEFAULT_CHART_TYPE ? undefined : newType,
    }]);
    setSearchParams(search, { replace: true });
    setChartType(newType);
  };

  const changeChartTypeBasedOnParams = () => {
    if (checkSubGroupSelected()) updateChartType(chartVarieties.HORIZONTAL);
    else if (chartType === chartVarieties.HORIZONTAL)
      updateChartType(chartVarieties.DONUT);
  };

  useEffect(() => {
    dispatch(setHeaderTitle('Report'));

    dispatch(setHeaderTabs(reportHeader));

    return () => {
      dispatch(setHeaderTabs([]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (group) {
      let params = getLocalStorage(group) || '';
      params = getLocalStorage(group) ? JSON.parse(params) : {}
      handleChangeParams(params);
    } else {
      handleGetReport();
    }

    const readChartType = () => {
      return (
        (getFilters(window.location.search, 'chartType') as string) ||
        DEFAULT_CHART_TYPE
      );
    };
    setChartType(readChartType);
  }, [group]);

  useEffect(() => {
    changeChartTypeBasedOnParams();
  }, []);

  const initialParams = useMemo(() => {
    return parseSearchParamsRouter(window.location.search);
  }, [group]);

  const checkSubGroupSelected = () => {
    const params = parseSearchParamsRouter(window.location.search);
    return !!params.subGroupBy;
  };

  const handleGetReport = (_params?: any) => {
    if (!group) return;
    const params = _params || initialParams;
    const thunkParams = Object.assign(
      {
        selectAll: params.selectAll,
        jobs: params.jobs,
        from: params?.dateRange?.start
          ? getDateTime(params?.dateRange?.start, DATE_YEAR_MONTH_DAY_FORMAT)
          : undefined,
        to: params?.dateRange?.end
          ? getDateTime(params?.dateRange?.end, DATE_YEAR_MONTH_DAY_FORMAT)
          : undefined,
      },
      params.subGroupBy
        ? {
            groupBy: params.subGroupBy,
            subGroupBy: group,
            hrs: params.hr,
            sources: params.source,
            statuses: params.status,
            regions: params.region,
            departments: params.department,
            experiences: params.experience,
            english: params.english,
          }
        : {
            groupBy: group,
            hr_id: params.hr,
            recruiter_id: params.recruiter,
            status_id: params.status,
            source_id: params.source,
            region_id: params.region,
            department_id: params.department,
            experience_id: params.experience,
            english_id: params.english,
        },
    );
    dispatch(getReportThunk(thunkParams));
  };

  const handleChangeParams = (params: any) => {
    if (!group) return;
    const queryParams = Object.keys(params).map((param: any) => {
      return { name: param, value: params[param] };
    });
    const search = getQueryStringFrom(queryParams);

    setSearchParams(search, { replace: true });

    changeChartTypeBasedOnParams();

    handleGetReport(params);
  };

  const prepareExportData = (chartData: ChartDataItem[], chartType: string) => {
    let result: ChartDataItem[][] = [];
    let i = 0;
    if (chartType === chartVarieties.HORIZONTAL)
      while (i < chartData.length) {
        result.push(chartData.slice(i, i + READABLE_ROWS_COUNT - 1));
        i += READABLE_ROWS_COUNT;
      }
    else result = [chartData];
    return result;
  };

  const prepareChartData = (report: Report) => {
    let commonCount = 0;

    if (checkSubGroupSelected()) {
      const stackBlockNames: string[] = [];

      const data = report.map(item => {
        let groupCount = 0;

        return {
          id: item.id,
          label: item.name || t('Not assign'),
          ...item.data!.reduce((acc, d) => {
            if (stackBlockNames.indexOf(d.name) === -1)
              stackBlockNames.push(d.name);
            groupCount += d.counts;
            commonCount += d.counts;
            return { ...acc, [d.name]: d.counts };
          }, {}),
          groupCount,
        };
      });

      return {
        stackBlockNames,
        data,
        commonCount,
        exportData: prepareExportData(data, chartType),
      };
    } else {
      const data = report
        .map(({ name, counts }) => {
          commonCount += counts;
          return {
            label: name || t('Not assign'),
            value: counts,
          };
        })
        .sort((a, b) => b.value - a.value);

      return {
        data,
        commonCount,
        exportData: prepareExportData(data, chartType),
      };
    }
  };

  const {
    data: chartData,
    stackBlockNames,
    commonCount,
    exportData,
  } = useMemo(() => prepareChartData(report), [report, t]);

  const hasReport = chartData.length > 0;

  const getSelectedReportItemId = (report: Report, label: string) => {
    for (let i = 0; i < report.length; i++) {
      const source = report[i];
      if (source.name === label) return source.id;
      else if (source.data !== undefined) {
        for (let j = 0; j < source.data.length; j++) {
          const subSource = source.data[j];
          if (subSource.name === label) return subSource.id;
        }
      }
    }
  };

  const getSearchParams = (
    data:
      | { field: string | undefined; value: number }
      | { field: string | undefined; value: number }[],
  ) => {
    const fieldsWithValue = Array.isArray(data) ? data : [data];

    let mergedParams = { ...reportParams };

    fieldsWithValue.forEach(item => {
      let clickedKey = item.field;
      let clickedValue: number | number[] = item.value;
      if (clickedKey === DEFAULT_GROUP) {
        clickedValue = [clickedValue];
        mergedParams.jobs = {
          value: clickedValue,
          switchValue: false
        };
      } else {
        clickedKey = `${clickedKey}_id`;
        mergedParams = { ...mergedParams, [clickedKey]: clickedValue };
      }
      mergedParams = { ...mergedParams, [clickedKey]: clickedValue };
    });
    const isHRUser =
      userRoles.some(role => [UserRole.DEV_HR, UserRole.HR].includes(role)) &&
      !userRoles.some(role => getRolesByPermissionToFullReportView().includes(role));

    if (isHRUser) {
      mergedParams = { ...mergedParams, hr_report: true };
    }

    const candidatesParams = mapReportParamsToCandidates(mergedParams);
    const queryParams = Object.entries(candidatesParams).map(
      ([name, value]) => {
        return { name, value };
      },
    );
    return getQueryStringFrom(queryParams);
  };

  const handleChartClick = (item: ChartDataItem, key?: string | undefined) => {
    const selectedId = getSelectedReportItemId(report, item.label);
    const clickedKey = key || reportParams.groupBy.toLowerCase();
    let clickedValue: number | number[];
    if (item.label === t(`${NOT_ASSIGN_LABEL}`)) {
      clickedValue = -1;
    } else if (selectedId === undefined) return;
    else {
      clickedValue = selectedId;
    }
    const searchParams = getSearchParams({
      field: clickedKey,
      value: clickedValue,
    });
    navigate(
      { pathname: RoutesConstant.CANDIDATES_GROUPS.SEARCH, search: searchParams },
      {
        state: {
          backUrl: location.pathname,
          backUrlQuery: location.search
        } as LocationState,
      });
  };

  const handleMultipleChartClick = (
    data: [MultipleChartDataItem, MultipleChartDataItem],
  ) => {
    const source = report.find(r => r.name === data[0].label);
    const subSource = source?.data!.find(d => d.name === data[1].label);

    if (!source?.id || !subSource?.id) return;

    const fieldsWithValue = [
      { field: data[0].field, value: source.id },
      { field: data[1].field, value: subSource.id },
    ];
    const searchParams = getSearchParams(fieldsWithValue);
    navigate(RoutesConstant.CANDIDATES_GROUPS.SEARCH + searchParams);
  };

  const handleDownloadChart = () => {
    const filename = `report-${group}.jpg`;
    const fileType = 'image/jpeg';
    captureRef.current.capture().then(data => {
      saveAs(data, filename ?? 'report.png', fileType ?? 'image/png');
    });
  };

  const hide = useMemo(
    () => ({
      jobs: group === DEFAULT_GROUP,
    }),
    [group],
  );

  return (
    <StyledReportPage>
      <ReportParamsForm
        key={group}
        onApply={handleChangeParams}
        hide={hide}
        group={group}
      />
      <CardComponent>
        {hasReport ? (
          <MemoizedChart
            chartLegendRef={chartLegendRef}
            chartContainerRef={chartContainerRef}
            chartData={chartData}
            chartType={chartType}
            updateChartType={updateChartType}
            checkSubGroupSelected={checkSubGroupSelected}
            handleMultipleChartClick={handleMultipleChartClick}
            commonCount={commonCount}
            handleChartClick={handleChartClick}
            stackBlockNames={stackBlockNames}
          />
        ) : (
          <NoResultComponent />
        )}
      </CardComponent>

      {hasReport && (
        <>
          <FloatButtonComponent onClick={handleDownloadChart}>
            <DownloadIcon
              width={DOWNLOAD_ICON_SIZE}
              height={DOWNLOAD_ICON_SIZE}
            />
          </FloatButtonComponent>
          <PictureCapturingComponent captureRef={captureRef}>
            {exportData?.map((part: ChartDataItem[], id: number) => (
              <ChartComponent
                key={`part-of-chart-${id}`}
                type={chartType}
                data={part}
                commonCount={commonCount}
                stackBlockNames={stackBlockNames}
                isAnimationActive={false}
                showLabels
                legendPortalRef={pictureLegendRef}
              />
            ))}
            <StyledCommonCountWrapper>
              {!!commonCount && (
                <StyledRightPanelTitle>{`${t(
                  'Number of records',
                )}: ${commonCount}`}</StyledRightPanelTitle>
              )}
            </StyledCommonCountWrapper>
            {chartType !== chartVarieties.HORIZONTAL && (
              <StyledPictureLegendContainer ref={pictureLegendRef} />
            )}
          </PictureCapturingComponent>
        </>
      )}
    </StyledReportPage>
  );
};

export default ReportPage;
